import React, { useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Users, KeyRound, Crown, Handshake,
  Mail, MessageSquare, CalendarClock, Bell,
  BarChart3, FileText, Wallet, Lock,
  Video, LifeBuoy, FolderOpen,
  Download, RefreshCw, Settings, Activity,
  Shield, Search, SlidersHorizontal, ChevronRight, GripVertical, Pencil,
  Truck, Home, Calendar, Star, Wrench, Camera, Map, Globe, Database, Zap,
  Tag, ClipboardList, DollarSign, Briefcase, Phone,
} from 'lucide-react';

const ICONS = {
  Users, KeyRound, Crown, Handshake, Mail, MessageSquare, CalendarClock, Bell,
  BarChart3, FileText, Wallet, Lock, Video, LifeBuoy, FolderOpen, Download,
  RefreshCw, Settings, Activity, Shield, Truck, Home, Calendar, Star, Wrench,
  Camera, Map, Globe, Database, Zap, Tag, ClipboardList, DollarSign, Briefcase, Phone,
};
const iconNameOf = (comp) => Object.keys(ICONS).find((k) => ICONS[k] === comp);

const GROUPS = [
  {
    id: 'gestao',
    title: 'Gestão de Acesso',
    icon: Users,
    chip: 'bg-blue-100 text-blue-700',
    bar: 'bg-blue-500',
    items: [
      { key: 'usuarios', label: 'Usuários', icon: Users },
      { key: 'permissoes', label: 'Permissões', icon: KeyRound },
      { key: 'leaders', label: 'Team Leaders', icon: Crown },
      { key: 'helper', label: 'Controle Helper', icon: Handshake },
      { key: 'vendas', label: 'Portal de Vendas', icon: Briefcase },
    ],
  },
  {
    id: 'comunicacao',
    title: 'Comunicação',
    icon: MessageSquare,
    chip: 'bg-green-100 text-green-700',
    bar: 'bg-green-500',
    items: [
      { key: 'emails', label: 'Emails', icon: Mail },
      { key: 'mensagens', label: 'Mensagens', icon: MessageSquare },
      { key: 'alertas', label: 'Alertas', icon: CalendarClock },
      { key: 'push', label: 'Push Notifications', icon: Bell, tab: 'push' },
    ],
  },
  {
    id: 'dados',
    title: 'Dados e Relatórios',
    icon: BarChart3,
    chip: 'bg-orange-100 text-orange-700',
    bar: 'bg-orange-500',
    items: [
      { key: 'dados', label: 'Dados PM', icon: BarChart3 },
      { key: 'laborbill', label: 'Labor Bill', icon: FileText },
      { key: 'comparar', label: 'Comparar Trabalhos', icon: Calendar },
      { key: 'contas', label: 'Contas a Pagar', icon: Wallet },
      { key: 'savannah', label: 'Despesas Savannah', icon: Home },
      { key: 'cofre', label: 'Cofre', icon: Lock },
    ],
  },
  {
    id: 'conteudo',
    title: 'Conteúdo',
    icon: Video,
    chip: 'bg-purple-100 text-purple-700',
    bar: 'bg-purple-500',
    items: [
      { key: 'videos', label: 'Vídeos', icon: Video },
      { key: 'suporte', label: 'Suporte', icon: LifeBuoy },
      { key: 'arquivos', label: 'Documentos (Arquivos)', icon: FolderOpen },
    ],
  },
  {
    id: 'ferramentas',
    title: 'Ferramentas',
    icon: Settings,
    chip: 'bg-teal-100 text-teal-700',
    bar: 'bg-teal-500',
    items: [
      { key: 'importar', label: 'Importar', icon: Download },
      { key: 'migracao', label: 'Migração', icon: RefreshCw },
      { key: 'sistema', label: 'Sistema', icon: Settings },
      { key: 'acessos', label: 'Logs e Acessos', icon: Activity },
    ],
  },
  {
    id: 'outros',
    title: 'Outros',
    icon: Shield,
    chip: 'bg-slate-200 text-slate-700',
    bar: 'bg-slate-400',
    items: [
      { key: 'seguros', label: 'Seguros', icon: Shield },
    ],
  },
];

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const GroupCard = ({ group, editMode, onItemClick, dragHandleProps, dragging, labels = {}, icons = {}, editingKey, draft, draftIcon, draftGroup, onStartEdit, onDraftChange, onDraftIconChange, onDraftGroupChange, onCommitEdit, groupsList = [] }) => {
  const GIcon = group.icon;
  return (
    <div
      className={`rounded-xl border bg-white shadow-sm overflow-hidden ${editMode ? 'border-dashed border-slate-300' : ''} ${dragging ? 'shadow-xl ring-2 ring-blue-400 opacity-95' : ''}`}
      data-testid={`admin-hub-group-${group.id}`}
    >
      <div className={`h-1 ${group.bar}`} />
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${group.chip}`}>
              <GIcon className="stroke-[1.75]" style={{ width: 18, height: 18 }} />
            </div>
            <span className="font-semibold text-slate-800 text-sm">{group.title}</span>
          </div>
          {editMode && (
            <button
              type="button"
              className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-grab active:cursor-grabbing touch-none"
              data-testid={`admin-hub-drag-${group.id}`}
              aria-label="Arrastar para reordenar"
              {...(dragHandleProps || {})}
            >
              <GripVertical className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="space-y-1">
          {group.items.length === 0 && editMode && (
            <p className="text-xs text-slate-400 italic px-2.5 py-2" data-testid={`admin-hub-empty-group-${group.id}`}>
              Vazio — mova sub-abas para cá
            </p>
          )}
          {group.items.map((it) => {
            const IIcon = ICONS[icons[it.key]] || it.icon;
            const displayLabel = labels[it.key] || it.label;
            if (editMode && editingKey === it.key) {
              return (
                <div key={it.key} className="border border-blue-300 bg-blue-50/60 rounded-lg p-2 space-y-2" data-testid={`admin-hub-rename-${it.key}`}>
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onCommitEdit(it);
                      if (e.key === 'Escape') onCommitEdit(it, true);
                    }}
                    placeholder={it.label}
                    className="w-full text-sm border border-blue-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                    data-testid={`admin-hub-rename-input-${it.key}`}
                  />
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 uppercase mb-1">Ícone</p>
                    <div className="flex flex-wrap gap-1" data-testid={`admin-hub-icon-picker-${it.key}`}>
                      {Object.entries(ICONS).map(([name, Ic]) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => onDraftIconChange(name)}
                          className={`p-1.5 rounded-md border transition-colors ${draftIcon === name ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300'}`}
                          data-testid={`admin-hub-icon-opt-${it.key}-${name}`}
                          aria-label={name}
                        >
                          <Ic style={{ width: 14, height: 14 }} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 uppercase mb-1">Classificação</p>
                    <select
                      value={draftGroup}
                      onChange={(e) => onDraftGroupChange(e.target.value)}
                      className="w-full text-xs border border-blue-300 rounded-md px-2 py-1.5 bg-white"
                      data-testid={`admin-hub-group-select-${it.key}`}
                    >
                      {groupsList.map((g) => (
                        <option key={g.id} value={g.id}>{g.title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => onCommitEdit(it)}
                      className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md py-1.5 font-medium"
                      data-testid={`admin-hub-save-${it.key}`}
                    >
                      Salvar
                    </button>
                    <button
                      type="button"
                      onClick={() => onCommitEdit(it, true)}
                      className="flex-1 text-xs bg-white border border-slate-300 text-slate-600 rounded-md py-1.5"
                      data-testid={`admin-hub-cancel-${it.key}`}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <button
                key={it.key}
                type="button"
                data-testid={`admin-hub-item-${it.key}`}
                onClick={() => (editMode ? onStartEdit(it, group.id) : onItemClick(it))}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left group transition-colors ${editMode ? 'hover:bg-blue-50 cursor-text' : 'hover:bg-slate-50 active:bg-slate-100'}`}
              >
                <span className="flex items-center gap-2.5 text-sm text-slate-700">
                  <IIcon className="text-slate-400 group-hover:text-slate-600" style={{ width: 16, height: 16 }} />
                  {displayLabel}
                  {labels[it.key] && labels[it.key] !== it.label && !editMode && null}
                </span>
                {editMode ? (
                  <Pencil className="h-3.5 w-3.5 text-blue-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SortableGroupCard = ({ group, onItemClick, ...renameProps }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <GroupCard
        group={group}
        editMode
        onItemClick={onItemClick}
        dragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
        {...renameProps}
      />
    </div>
  );
};

const AdminHub = ({ language = 'pt-BR', onNavigate, onNavigateTab }) => {
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [labels, setLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('adminHubLabels') || '{}') || {}; } catch (e) { return {}; }
  });
  const [icons, setIcons] = useState(() => {
    try { return JSON.parse(localStorage.getItem('adminHubIcons') || '{}') || {}; } catch (e) { return {}; }
  });
  const [itemGroups, setItemGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('adminHubItemGroups') || '{}') || {}; } catch (e) { return {}; }
  });
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [draftIcon, setDraftIcon] = useState(null);
  const [draftGroup, setDraftGroup] = useState('');
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('adminHubOrder') || 'null');
      if (Array.isArray(saved) && saved.length) return saved;
    } catch (e) { /* ignore */ }
    return GROUPS.map((g) => g.id);
  });

  const orderedGroups = useMemo(() => {
    const buckets = Object.fromEntries(GROUPS.map((g) => [g.id, []]));
    GROUPS.forEach((g) => g.items.forEach((it) => {
      const target = itemGroups[it.key] && buckets[itemGroups[it.key]] ? itemGroups[it.key] : g.id;
      buckets[target].push(it);
    }));
    const byId = Object.fromEntries(GROUPS.map((g) => [g.id, { ...g, items: buckets[g.id] }]));
    const known = order.filter((id) => byId[id]).map((id) => byId[id]);
    const missing = GROUPS.filter((g) => !order.includes(g.id)).map((g) => byId[g.id]);
    return [...known, ...missing];
  }, [order, itemGroups]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedGroups.map((g) => g.id);
    const newIds = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    setOrder(newIds);
    localStorage.setItem('adminHubOrder', JSON.stringify(newIds));
  };

  const q = norm(search);
  const visibleGroups = orderedGroups
    .map((g) => ({ ...g, items: q ? g.items.filter((it) => norm(labels[it.key] || it.label).includes(q) || norm(it.label).includes(q) || norm(g.title).includes(q)) : g.items }))
    .filter((g) => (editMode && !q) || g.items.length > 0);

  const startEdit = (item, groupId) => {
    setEditingKey(item.key);
    setDraft(labels[item.key] || item.label);
    setDraftIcon(icons[item.key] || iconNameOf(item.icon) || null);
    setDraftGroup(itemGroups[item.key] || groupId);
  };

  const commitEdit = (item, cancel = false) => {
    if (!cancel) {
      const value = (draft || '').trim();
      const nextLabels = { ...labels };
      if (!value || value === item.label) {
        delete nextLabels[item.key];
      } else {
        nextLabels[item.key] = value;
      }
      setLabels(nextLabels);
      localStorage.setItem('adminHubLabels', JSON.stringify(nextLabels));

      const defaultIcon = iconNameOf(item.icon);
      const nextIcons = { ...icons };
      if (draftIcon && draftIcon !== defaultIcon) {
        nextIcons[item.key] = draftIcon;
      } else {
        delete nextIcons[item.key];
      }
      setIcons(nextIcons);
      localStorage.setItem('adminHubIcons', JSON.stringify(nextIcons));

      const homeGroup = GROUPS.find((g) => g.items.some((i) => i.key === item.key))?.id;
      const nextGroups = { ...itemGroups };
      if (draftGroup && draftGroup !== homeGroup) {
        nextGroups[item.key] = draftGroup;
      } else {
        delete nextGroups[item.key];
      }
      setItemGroups(nextGroups);
      localStorage.setItem('adminHubItemGroups', JSON.stringify(nextGroups));
    }
    setEditingKey(null);
    setDraft('');
    setDraftIcon(null);
    setDraftGroup('');
  };

  const renameProps = {
    labels,
    icons,
    editingKey,
    draft,
    draftIcon,
    draftGroup,
    onStartEdit: startEdit,
    onDraftChange: setDraft,
    onDraftIconChange: setDraftIcon,
    onDraftGroupChange: setDraftGroup,
    onCommitEdit: commitEdit,
    groupsList: GROUPS,
  };

  const go = (item) => {
    if (item.tab) {
      onNavigateTab && onNavigateTab(item.tab);
    } else {
      onNavigate && onNavigate(item.key);
    }
  };

  return (
    <div data-testid="admin-hub">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-slate-800">Admin</h2>
        <p className="text-sm text-slate-500">
          {language === 'pt-BR' ? 'Gerencie todas as áreas do sistema.' : 'Manage all areas of the system.'}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="admin-hub-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={language === 'pt-BR' ? 'Buscar funcionalidade...' : 'Search feature...'}
            className="pl-9"
          />
        </div>
        <Button
          data-testid="admin-hub-customize-btn"
          variant={editMode ? 'default' : 'outline'}
          size="sm"
          className="text-xs sm:self-center"
          onClick={() => setEditMode(!editMode)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
          {editMode ? (language === 'pt-BR' ? 'Concluir' : 'Done') : (language === 'pt-BR' ? 'Personalizar layout' : 'Customize layout')}
        </Button>
      </div>

      {visibleGroups.length === 0 && (
        <p className="text-center text-slate-500 py-10" data-testid="admin-hub-empty">
          {language === 'pt-BR' ? 'Nenhuma funcionalidade encontrada.' : 'No feature found.'}
        </p>
      )}

      {editMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleGroups.map((g) => g.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-hub-grid-edit">
              {visibleGroups.map((g) => (
                <SortableGroupCard key={g.id} group={g} onItemClick={go} {...renameProps} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleGroups.map((g) => (
            <GroupCard key={g.id} group={g} editMode={false} onItemClick={go} labels={labels} icons={icons} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminHub;
